import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Truck, Handshake, DollarSign, Package, Phone, HelpCircle, ArrowRight } from 'lucide-react';
const QUICK_ACTIONS = [{
  icon: Handshake,
  title: 'Want to Lease On With FSG?',
  desc: 'For owner-operators looking for dispatch support, clear communication, and a compliance-first carrier.',
  cta: 'Apply to Lease On',
  link: '/lease-on-with-fsg',
  type: 'route'
}, {
  icon: Truck,
  title: 'Have Your Own Authority?',
  desc: 'For owner-operators and small carriers who need dispatch and back-office support.',
  cta: 'Start Dispatch',
  link: '/dispatch-services',
  type: 'route'
}, {
  icon: DollarSign,
  title: 'What Can I Earn?',
  desc: 'See recent dispatch activity, RPM, and weekly revenue context.',
  cta: 'View Pay Info',
  link: '#activity',
  type: 'hash'
}, {
  icon: HelpCircle,
  title: 'Freight, Lanes & Home Time',
  desc: 'See the freight FSG usually runs and how lane preferences are handled.',
  cta: 'View Driver FAQs',
  link: '#faq',
  type: 'hash'
}, {
  icon: Package,
  title: 'Need a Trailer?',
  desc: 'Ask about trailer rental options and setup.',
  cta: 'Contact FSG',
  link: '/contact',
  type: 'route'
}, {
  icon: Phone,
  title: 'Talk to FSG',
  desc: 'Call with questions before applying.',
  cta: 'Call 234-602-8424',
  link: 'tel:2346028424',
  type: 'href'
}];
export function Hero() {
  const renderAction = (action: (typeof QUICK_ACTIONS)[0]) => {
    const content = <div className="bg-white border border-steel p-5 rounded-sm hover:border-red hover:shadow-lg transition-all group h-full flex flex-col" data-id="element-262">
        <action.icon className="w-6 h-6 text-red mb-3 group-hover:scale-110 transition-transform" data-id="element-263" />
        <h3 className="text-navy font-bold text-sm md:text-base mb-1 leading-snug" data-id="element-264">
          {action.title}
        </h3>
        <p className="text-body text-xs md:text-sm font-medium mb-3 flex-grow" data-id="element-265">
          {action.desc}
        </p>
        <span className="text-red font-bold text-xs uppercase tracking-wider flex items-center gap-1 group-hover:text-red-700 transition-colors mt-auto" data-id="element-266">
          {action.cta} <ArrowRight className="w-3 h-3" data-id="element-267" />
        </span>
      </div>;
    if (action.type === 'route') {
      return <Link to={action.link} key={action.title} className="block h-full" data-id="element-268">
          {content}
        </Link>;
    }
    if (action.type === 'hash') {
      return <a href={action.link} key={action.title} onClick={e => {
        e.preventDefault();
        document.querySelector(action.link)?.scrollIntoView({
          behavior: 'smooth'
        });
      }} className="block h-full" data-id="element-269">
          {content}
        </a>;
    }
    return <a href={action.link} key={action.title} className="block h-full" data-id="element-270">
        {content}
      </a>;
  };
  return <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden bg-off-white" data-id="element-271">
      <div className="container-custom relative z-10" data-id="element-272">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-273">
          {/* Left: Text & CTAs */}
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-5" data-id="element-274">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-navy leading-[1.05] mb-6 tracking-tight font-['Barlow']" data-id="element-275">
              Drive With FSG or Get Dispatch Support That Keeps Your Truck
              Moving
            </h1>

            <p className="text-lg md:text-xl text-body font-medium mb-8 max-w-xl leading-relaxed" data-id="element-276">
              FSG Transport helps owner-operators and small fleets with lease-on
              opportunities, dispatch support, load planning, broker calls,
              paperwork, billing, and back-office follow-through, with no forced
              dispatch.
            </p>

            <div className="flex flex-wrap items-center gap-4" data-id="element-277">
              <Link to="/lease-on-with-fsg" className="btn-primary" data-id="element-278">
                Apply to Lease On
              </Link>
              <a href="tel:2346028424" className="btn-outline-dark flex items-center gap-2" data-id="element-279">
                <Phone className="w-4 h-4" data-id="element-280" />
                234-602-8424
              </a>
            </div>
          </motion.div>

          {/* Right: Quick Actions Command Center */}
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-7" data-id="element-281">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-282">
              {QUICK_ACTIONS.map(renderAction)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}